import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sendChatMessage, resetConversation } from '../services/chatService';
import '../styles/Chat.css';

const CONVERSATION_KEY = 'worksheet-chat';

function Chat() {
  const { isAuthenticated, user } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: "Hi! I'm your worksheet assistant. Tell me a bit about your child — what grade are they in and what subject would you like to focus on?",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null); // null | 'generating' | 'done' | 'error'
  const [lastActivityId, setLastActivityId] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to newest message — must be before any early returns
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, generationStatus]);

  // Guard — placed after all hooks to satisfy Rules of Hooks
  if (!isAuthenticated) {
    return null;
  }

  const appendMessage = (role, text) => {
    setMessages(prev => [...prev, { role, text }]);
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    setInput('');
    setError(null);
    appendMessage('user', trimmed);
    setLoading(true);

    try {
      const result = await sendChatMessage(trimmed, CONVERSATION_KEY);
      appendMessage('assistant', result.reply);

      if (result.worksheetReady) {
        if (result.generation?.activity_id) {
          setLastActivityId(result.generation.activity_id);
          setGenerationStatus('done');
        } else if (result.generationError) {
          setGenerationStatus('error');
          setError(`Worksheet queued but generation failed: ${result.generationError}`);
        } else {
          setGenerationStatus('generating');
        }
      }
    } catch (err) {
      console.error('Chat error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewConversation = () => {
    resetConversation(CONVERSATION_KEY);
    setMessages([
      {
        role: 'assistant',
        text: "Starting fresh! What grade level and subject would you like to work on?",
      },
    ]);
    setInput('');
    setError(null);
    setGenerationStatus(null);
    setLastActivityId(null);
    inputRef.current?.focus();
  };

  const isSessionDone = generationStatus === 'done' || generationStatus === 'error';

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-header-text">
          <h2>Worksheet Assistant</h2>
          <p>Chat with the AI to design a personalized worksheet for {user?.username || 'your child'}.</p>
        </div>
        <button
          className="chat-new-btn"
          onClick={handleNewConversation}
          title="Start a new conversation"
        >
          New Chat
        </button>
      </div>

      <div className="chat-messages" role="log" aria-live="polite">
        {messages.map((msg, i) => (
          <div key={i} className={`chat-bubble chat-bubble--${msg.role}`}>
            <span className="chat-bubble__label">
              {msg.role === 'user' ? (user?.username || 'You') : 'Assistant'}
            </span>
            <p className="chat-bubble__text">{msg.text}</p>
          </div>
        ))}

        {loading && (
          <div className="chat-bubble chat-bubble--assistant chat-bubble--loading">
            <span className="chat-bubble__label">Assistant</span>
            <span className="chat-typing">
              <span /><span /><span />
            </span>
          </div>
        )}

        {generationStatus === 'done' && lastActivityId && (
          <div className="chat-status chat-status--success">
            Worksheet is being generated! Activity ID: <strong>{lastActivityId}</strong>.
            Check your Activity History shortly.
          </div>
        )}

        {generationStatus === 'error' && (
          <div className="chat-status chat-status--error">
            The worksheet prompt was ready but generation encountered an error. Please try again.
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {error && <div className="chat-error">{error}</div>}

      <div className="chat-input-row">
        <textarea
          ref={inputRef}
          className="chat-input"
          rows={2}
          placeholder={isSessionDone ? 'Start a new conversation above' : 'Type a message… (Enter to send, Shift+Enter for new line)'}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading || isSessionDone}
          aria-label="Chat message input"
        />
        <button
          className="chat-send-btn"
          onClick={handleSend}
          disabled={loading || !input.trim() || isSessionDone}
          aria-label="Send message"
        >
          {loading ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

export default Chat;
